/**
 * 資金移動(口座間振替)の入力。
 * 両口座に出金/入金として現れるが、売上でも経費でもなくPLには含まれない。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import { formatAmountInput, parseAmount } from '@core/lib/money';
import { createTransfer } from './transferRepo';
import type { CashAccount } from '@core/lib/types';

export default function TransferForm({
  accounts,
  onClose,
  onSaved,
}: {
  accounts: CashAccount[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const { fiscal, defaultCompanyId } = useMasters();
  const toast = useToast();

  const [fromId, setFromId] = useState(accounts[0]?.id ?? '');
  const [toId, setToId] = useState(accounts[1]?.id ?? '');
  const [date, setDate] = useState(todayStr());
  const [amountText, setAmountText] = useState('');
  const [description, setDescription] = useState('');
  const [settled, setSettled] = useState(true);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!user) return;
    const amount = parseAmount(amountText);
    if (amount === null) {
      toast('金額を正しく入力してください(1円以上の整数)');
      return;
    }
    setBusy(true);
    try {
      await createTransfer({
        input: {
          companyId: defaultCompanyId,
          fromCashAccountId: fromId,
          toCashAccountId: toId,
          date,
          amount,
          description,
          settled,
        },
        userId: user.id,
        fiscalStartMonth: fiscal.start_month,
      });
      toast('資金移動を登録しました(PLには計上されません)');
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(640px, 92vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3>資金移動(口座間振替)</h3>
      </div>

      <div className="form-grid">
        <div>
          <label>移動元(出金)</label>
          <select value={fromId} onChange={(e) => setFromId(e.target.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>移動先(入金)</label>
          <select value={toId} onChange={(e) => setToId(e.target.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>日付</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label>金額(円)</label>
          <input
            inputMode="numeric"
            value={amountText}
            onChange={(e) => setAmountText(formatAmountInput(e.target.value))}
            placeholder="1,000,000"
          />
        </div>
        <div>
          <label>状態</label>
          <select value={settled ? '1' : '0'} onChange={(e) => setSettled(e.target.value === '1')}>
            <option value="1">実行済み</option>
            <option value="0">予定(これから動かす)</option>
          </select>
        </div>
        <div className="fw">
          <label>内容(任意)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="例)給与資金の移動"
          />
        </div>
      </div>

      <button className="btn-green" onClick={() => void save()} disabled={busy}>
        {busy ? '登録中…' : '登録する'}
      </button>
      <p className="hint" style={{ marginTop: 10 }}>
        口座間の付け替えは売上でも経費でもないため、<b>PL(損益)には計上されません</b>。
        両方の口座の入出金一覧に出金/入金として表示されます。
      </p>
    </Modal>
  );
}
